import { CheckCircle, Group, Timer } from "@mui/icons-material";
import { Avatar, Box, Card, CardContent, Grid, LinearProgress, Paper, Stack, Typography } from "@mui/material";
import { useMediaQuery } from "@uidotdev/usehooks";
import dayjs from "dayjs";
import { useEffect, useRef, useState } from "react";
import { selectCurrentCompany } from "../../../../../../redux/features/user/selectors/userState.selectors";
import { useAppSelector } from "../../../../../../redux/hooks/redux";
import { formatDate } from "../../../../../../utils";
import { BaseModalComponent } from "../../../../../common/BaseModalComponent";
import { WorkforceDetail } from "../../../../../employees/components/WorkforceDetail";
import { JobChatView } from "../../../../../job-chats";
import { colors, alphaVar } from "../../../../../ui/colors";
import { getStatusLabel } from "./helper/getStatusLabel";

const jobCardSurfaceStyles = {
  borderRadius: "16px",
  bgcolor: "background.paper",
  border: `1px solid ${alphaVar(colors.gray[100], 0.55)}`,
  boxShadow: "none",
};

export default function InProgressBusiness({ data, focusJobChat = false }) {
  const currentEvent = data?.events?.[0] || {};
  const staff = data?.events?.[0]?.workforce || [];

  const currentCompany = useAppSelector(selectCurrentCompany);
  const companyTimeZone = currentCompany?.timeZone || "UTC";
  const jobChatCompanyId = data?.companyId ?? currentCompany?.id;

  const [employeeProgress, setEmployeeProgress] = useState({});
  const jobChatRef = useRef(null);

  useEffect(() => {
    if (!focusJobChat) return;
    const timeout = window.setTimeout(() => {
      const chat = jobChatRef.current;
      if (!chat) return;

      chat.focus({ preventScroll: true });
      let scrollContainer = chat.parentElement;
      while (scrollContainer) {
        const { overflowY } = window.getComputedStyle(scrollContainer);
        if (["auto", "scroll"].includes(overflowY) && scrollContainer.scrollHeight > scrollContainer.clientHeight) break;
        scrollContainer = scrollContainer.parentElement;
      }

      if (scrollContainer) {
        const top = scrollContainer.scrollTop + chat.getBoundingClientRect().top - scrollContainer.getBoundingClientRect().top - 16;
        scrollContainer.scrollTo({ top, behavior: "smooth" });
      } else {
        chat.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 350);
    return () => window.clearTimeout(timeout);
  }, [focusJobChat, currentEvent?.uid]);

  const isMobile = useMediaQuery("(max-width:600px)");

  useEffect(() => {
    const progressData = {};
    const eventStart = currentEvent?.startDate ? dayjs(currentEvent.startDate) : null;

    staff.forEach((employee) => {
      const jobEvent = employee.WorkforcesByJobEvents || {};

      const startAtRaw = jobEvent.startAt || null;
      const endAtRaw = jobEvent.endAt || null;

      const startAt = startAtRaw ? dayjs(startAtRaw) : null;
      const endAt = endAtRaw ? dayjs(endAtRaw) : null;

      let status = 0;
      if (!startAt && !endAt && eventStart && dayjs().isAfter(eventStart)) status = 3;
      if (startAt && !endAt) status = 1;
      if (endAt) status = 2;

      progressData[employee.uid] = {
        status,
        startAtRaw,
        endAtRaw,
        startTime: startAt,
        endTime: endAt,
        elapsedTime: startAt && !endAt ? dayjs().diff(startAt, "second") : 0,
        role: jobEvent.jobWorkforceRole,
      };
    });

    setEmployeeProgress(progressData);
  }, [staff, currentEvent?.startDate]);

  useEffect(() => {
    const interval = setInterval(() => {
      setEmployeeProgress((prev) => {
        const updated = { ...prev };

        Object.keys(updated).forEach((id) => {
          if (updated[id].status === 1 && updated[id].startTime) {
            updated[id].elapsedTime = dayjs().diff(updated[id].startTime, "second");
          }
        });

        return updated;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  const formatElapsed = (s) => {
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    return h > 0 ? `${h}h ${m}m ${sec}s` : `${m}m ${sec}s`;
  };

  const getJobProgress = () => {
    if (!staff.length) return 0;
    const done = Object.values(employeeProgress).filter((p) => p.status === 2).length;
    return (done / staff.length) * 100;
  };

  return (
    <Box
      sx={{
        width: "100%",
        px: 3,
        py: 3,
        backgroundColor: isMobile ? "var(--fx-surface-base, #FFFFFF)" : "var(--fx-surface-sunken, #F8FAFC)" /*General container*/,
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "minmax(320px, 0.9fr) minmax(0, 1.35fr)" },
        gridTemplateAreas: {
          xs: '"left" "chat"',
          md: '"left chat"',
        },
        gap: { xs: 2, md: 3 },
      }}
    >
      <Box sx={{ gridArea: "left", display: "flex", flexDirection: "column", gap: { xs: 1.5, md: 2 }, alignSelf: "start" }}>
      {/* STAFF PROGRESS */}
      <Typography variant="h6" fontWeight={600} sx={{ order: 2, fontSize: "0.95rem", color: colors.gray[700], mb: 0, display: "flex", alignItems: "center", gap: 0.75 }}>
        <Group sx={{ color: colors.orange.base, fontSize: 19 }} />
        Assigned Team ({staff.length})
      </Typography>

      <Grid
        container
        spacing={2}
        sx={{
          order: 3,
          alignSelf: "start",
          maxHeight: 220,
          overflowY: staff.length > 3 ? "auto" : "visible",
          pr: staff.length > 3 ? 0.75 : 0,
          scrollbarWidth: "thin",
          "&::-webkit-scrollbar": { width: 6 },
          "&::-webkit-scrollbar-thumb": {
            bgcolor: colors.gray[200],
            borderRadius: 999,
          },
        }}
      >
        {staff.map((employee) => {
          const p = employeeProgress[employee.uid] || {};
          const s = getStatusLabel(p.status);
          const fullName = `${employee.firstName} ${employee.lastName}`;
          return (
            <BaseModalComponent
              modalKey={`Job-In-Progress-Workforce-${employee?.uid}`}
              modalLabel="Workforces In Progress"
              allowInsidePreventModal
              elementTrigger={
                <Grid item xs={12} sm={6} md={12} key={employee.uid}>
                  <Card
                    sx={{
                      ...jobCardSurfaceStyles,
                      height: "100%",
                      cursor: "pointer",
                      transition: "transform 160ms ease, border-color 160ms ease, background-color 160ms ease",
                      "&:hover": {
                        transform: "translateY(-2px)",
                        borderColor: alphaVar(colors.orange.base, 0.28),
                        backgroundColor: alphaVar(colors.orange[50], 0.18),
                      },
                      "&:active": {
                        transform: "translateY(0) scale(0.99)",
                      },
                    }}
                  >
                    <CardContent sx={{ p: 1.25, "&:last-child": { pb: 1.25 } }}>
                      <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1} mb={0.5}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Avatar src={employee.avatar} sx={{ width: 32, height: 32 }} />
                          <Box>
                            <Typography fontWeight={600} sx={{ fontSize: "0.76rem" }}>{fullName}</Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.68rem" }}>
                              {employee.WorkforcesByJobEvents?.jobWorkforceRole || "Staff"}
                            </Typography>
                          </Box>
                        </Stack>
                        {p.status === 0 ? (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontSize: "0.58rem", fontWeight: 600, lineHeight: 1.2, alignSelf: "flex-end", mb: 0.25, whiteSpace: "nowrap" }}
                          >
                            {s.label}
                          </Typography>
                        ) : (
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={0.35}
                            sx={{
                              color: s.color,
                              alignSelf: "center",
                              lineHeight: 1,
                              "& .MuiSvgIcon-root": {
                                fontSize: 14,
                              },
                            }}
                          >
                            {s.icon}
                            <Typography
                              variant="caption"
                              sx={{
                                color: s.color,
                                fontSize: "0.65rem",
                                fontWeight: 700,
                                lineHeight: 1,
                                whiteSpace: "nowrap",
                              }}
                            >
                              {s.label}
                            </Typography>
                          </Stack>
                        )}
                      </Stack>

                      {p.status === 1 && (
                        <Box sx={{ bgcolor: alphaVar(s.bg, 0.65), px: 1, py: 0.6, borderRadius: 1.5, display: "flex", alignItems: "center", justifyContent: "center", gap: 0.75 }}>
                          <Timer sx={{ color: s.color, fontSize: 16 }} />
                          <Typography fontWeight={700} color={s.color} sx={{ fontSize: "0.7rem" }}>
                            {formatElapsed(p.elapsedTime)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.62rem" }}>
                            Started at {p.startAtRaw ? formatDate(p.startAtRaw, companyTimeZone, "h:mm a") : "N/A"}
                          </Typography>
                        </Box>
                      )}

                      {p.status === 2 && (
                        <Box sx={{ bgcolor: alphaVar(s.bg, 0.65), px: 1, py: 0.6, borderRadius: 1.5, display: "flex", alignItems: "center", justifyContent: "center", gap: 0.75, flexWrap: "wrap" }}>
                          <CheckCircle sx={{ color: s.color, fontSize: 16 }} />
                          <Typography fontWeight={700} color={s.color} sx={{ fontSize: "0.7rem" }}>
                            Completed
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.62rem" }}>
                            Started at {p.startAtRaw ? formatDate(p.startAtRaw, companyTimeZone, "h:mm a") : "N/A"}
                            {". "}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.62rem" }}>
                            Finished at {p.endAtRaw ? formatDate(p.endAtRaw, companyTimeZone, "h:mm a") : "N/A"}
                          </Typography>
                        </Box>
                      )}

                      {/* Waiting for punch already communicates this state; keep the non-interactive
                          "Waiting to start" panel hidden so it is not mistaken for a button.
                      {p.status === 0 && (
                        <Box sx={{ bgcolor: alphaVar(colors.gray[100], 0.45), px: 1, py: 0.6, borderRadius: 1.5, display: "flex", alignItems: "center", justifyContent: "center", gap: 0.75 }}>
                          <Schedule sx={{ color: s.color, fontSize: 16 }} />
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.65rem" }}>
                            Waiting to start
                          </Typography>
                        </Box>
                      )} */}

                    </CardContent>
                  </Card>
                </Grid>
              }
            >
              <WorkforceDetail workforce={employee} />
            </BaseModalComponent>
          );
        })}
      </Grid>

      {/* SUMMARY */}
      {staff.length > 0 && (
        <Paper
          elevation={0}
          sx={{
            ...jobCardSurfaceStyles,
            order: 1,
            p: 1.5,
            mt: 0,
            height: "auto",
            alignSelf: "stretch",
          }}
        >
          <Typography variant="h6" fontWeight={600} color={colors.gray[800]} gutterBottom sx={{ fontSize: "0.95rem", mb: 0.75 }}>
            Job Summary
          </Typography>
          <Grid container spacing={0.5}>
            <Grid item xs={4} textAlign="center">
              <Typography variant="h4" color={colors.gray[700]} sx={{ fontSize: "1.15rem" }}>
                {staff.length}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.58rem", whiteSpace: "nowrap" }}>
                Total Employees
              </Typography>
            </Grid>
            <Grid item xs={4} textAlign="center">
              <Typography variant="h4" color={colors.blue.base} sx={{ fontSize: "1.15rem" }}>
                {Object.values(employeeProgress).filter((p) => p.status === 1).length}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.58rem", whiteSpace: "nowrap" }}>
                Working Now
              </Typography>
            </Grid>
            <Grid item xs={4} textAlign="center">
              <Typography variant="h4" color={colors.success.base} sx={{ fontSize: "1.15rem" }}>
                {Object.values(employeeProgress).filter((p) => p.status === 2).length}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.58rem", whiteSpace: "nowrap" }}>
                Completed
              </Typography>
            </Grid>
            <Grid item xs={12} sx={{ mt: 1.8 }}>
              <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 1 }}>
                <Typography variant="h6" fontWeight={600} color={colors.gray[800]} sx={{ fontSize: "0.95rem" }}>
                  Visit In Progress
                </Typography>
                <Typography variant="h4" color={colors.orange.base} sx={{ fontSize: "1rem" }}>
                  {Math.round(getJobProgress())}%
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={getJobProgress()}
                sx={{
                  width: "100%",
                  height: 4,
                  mx: "auto",
                  mt: 1.1,
                  borderRadius: 2,
                  backgroundColor: colors.gray[200],
                  "& .MuiLinearProgress-bar": { backgroundColor: colors.orange.base },
                }}
              />
            </Grid>
          </Grid>
        </Paper>
      )}
      </Box>

      {/* TEAM CHAT */}
      {currentEvent?.uid && jobChatCompanyId && (
        <Paper
          elevation={0}
          ref={jobChatRef}
          tabIndex={-1}
          sx={{
            ...jobCardSurfaceStyles,
            gridArea: "chat",
            mt: 0,
            height: { xs: 360, md: "auto" },
            minHeight: { xs: 360, md: 430 },
            display: "flex",
            flexDirection: "column",
            alignSelf: { xs: "start", md: "stretch" },
            contain: { xs: "none", md: "size" },
            scrollMarginTop: 16,
            overflow: "hidden",
          }}
        >
          <Box sx={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
            <JobChatView
              jobEventUid={currentEvent.uid}
              companyId={jobChatCompanyId}
              status={currentEvent.status}
              jobAlias={data?.alias}
              headerTitle="Job Chat"
            />
          </Box>
        </Paper>
      )}
    </Box>
  );
}
